import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, KeyRound } from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Input } from "../ui/input";

const cardMotion = {
  hidden: { opacity: 0, y: 22 },
  show: (index) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, delay: index * 0.06, ease: "easeOut" },
  }),
};

const ResumeBlock = ({ onResumeByCode }) => {
  const [code, setCode] = useState("");
  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) return;
    onResumeByCode(trimmed);
  };
  return (
    <form
      className="mt-8 flex flex-wrap items-end gap-3 rounded-[1.4rem] border border-[#d8ece4] bg-white/85 p-4 shadow-[0_14px_36px_-32px_rgba(15,118,110,0.32)] sm:p-5"
      data-testid="formations-qcm-resume-block"
      onSubmit={handleSubmit}
    >
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#0f766e]">
        <KeyRound className="h-3.5 w-3.5" />
        Reprendre un test
      </div>
      <div className="flex-1 min-w-[180px]">
        <label className="sr-only" htmlFor="formations-qcm-resume-input">Code secret</label>
        <Input
          autoCapitalize="characters"
          className="font-mono tracking-[0.18em] uppercase"
          data-testid="formations-qcm-resume-input"
          id="formations-qcm-resume-input"
          maxLength={32}
          onChange={(e) => setCode(e.target.value)}
          placeholder="Ex. QCMRE-TVSDK3"
          value={code}
        />
      </div>
      <Button
        className="rounded-full bg-[#0f766e] text-white hover:bg-[#149289]"
        data-testid="formations-qcm-resume-submit"
        disabled={!code.trim()}
        type="submit"
      >
        Continuer mon test
        <ArrowRight className="ml-2 h-4 w-4" />
      </Button>
    </form>
  );
};

export const QcmSection = ({ qcms, onOpenQcm, onResumeByCode }) => {
  return (
    <section className="px-4 py-10 sm:px-6 lg:px-8 lg:py-16" data-testid="formations-qcm-section">
      <div className="mx-auto max-w-7xl rounded-[2.6rem] border border-[#d9e7d5]/85 bg-[linear-gradient(180deg,_rgba(240,253,250,0.9)_0%,_rgba(255,255,255,0.98)_100%)] px-6 py-10 shadow-[0_30px_86px_-58px_rgba(15,118,110,0.26)] sm:px-10 lg:px-12">
        <div className="max-w-3xl">
          <div className="text-sm font-semibold uppercase tracking-[0.22em] text-[#0f766e]" data-testid="formations-qcm-eyebrow">
            Tests QCM gratuits
          </div>
          <h2 className="bim-display mt-4 text-4xl font-semibold tracking-tight text-slate-950 sm:text-5xl" data-testid="formations-qcm-title">
            Vérifiez rapidement votre niveau avant de choisir votre formation.
          </h2>
          <p className="mt-5 text-base leading-8 text-slate-600 sm:text-lg" data-testid="formations-qcm-description">
            Vos réponses sont sauvegardées automatiquement. Conservez votre code secret pour reprendre ou consulter votre test plus tard, sans compte ni email.
          </p>
        </div>

        {typeof onResumeByCode === "function" ? <ResumeBlock onResumeByCode={onResumeByCode} /> : null}

        <div className="mt-10 grid gap-5 sm:grid-cols-2 xl:grid-cols-3" data-testid="formations-qcm-grid">
          {qcms.map((qcm, index) => (
            <motion.div custom={index} initial="hidden" key={qcm.id} viewport={{ once: true, amount: 0.2 }} whileInView="show" variants={cardMotion}>
              <Card className="h-full rounded-[1.8rem] border border-[#d8ece4] bg-white/92 shadow-[0_24px_60px_-46px_rgba(15,118,110,0.22)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_30px_70px_-44px_rgba(15,118,110,0.26)]" data-testid={`formations-qcm-card-${qcm.id}`}>
                <CardContent className="flex h-full flex-col p-6">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#0f766e]" data-testid={`formations-qcm-card-eyebrow-${qcm.id}`}>
                    Test gratuit
                  </div>
                  <h3 className="mt-4 text-xl font-semibold leading-tight text-slate-950" data-testid={`formations-qcm-card-title-${qcm.id}`}>
                    {qcm.title}
                  </h3>
                  <p className="mt-4 text-sm leading-7 text-slate-600" data-testid={`formations-qcm-card-description-${qcm.id}`}>
                    {qcm.description}
                  </p>
                  <Button className="mt-6 h-11 rounded-full border-[#d8ece4] bg-[#f8fcf3] text-[#0f766e] hover:bg-[#ecfdf5] hover:text-[#0f766e]" data-testid={`formations-qcm-card-button-${qcm.id}`} onClick={() => onOpenQcm(qcm)} type="button" variant="outline">
                    Prendre le test
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};